import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button, Icon, Modal } from 'src/components';
import { TrackingApi } from 'src/api';
import { CalendarReminderTrackingAction } from 'src/api/tracking/types';
import { useUserContext } from 'src/context/user';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import {
  CalendarProvider,
  ClockTime,
  CUSTOM_DEFAULT_TIME,
  LUNCH_TIME,
  MORNING_TIME,
  REMINDER_HOURS_IN_A_MONTH,
  REMINDER_REPEAT_COUNT,
  SlotId,
  browserTimeZone,
  buildCalendarEvent,
  monthlyNewWords,
  parseTimeInputValue,
  roundToNearestHalfHour,
  toTimeInputValue,
} from './event';
import { openProviderCalendar } from './links';
import { Mascot } from './Mascot';
import { addToCalendarReminderStorageEntry } from './storage';
import * as S from './styles';

const msgs = defineMessages({
  wellDone: {
    id: 'calendar_reminder_well_done',
    defaultMessage:
      'Well done! {count, plural, one {# {language} word} other {# {language} words}} learnt so far',
  },
  remember: {
    id: 'calendar_reminder_remember',
    defaultMessage: 'Remember',
  },
  percent: {
    id: 'calendar_reminder_percent',
    defaultMessage: '200%',
  },
  better: {
    id: 'calendar_reminder_better',
    defaultMessage: 'better',
  },
  recall: {
    id: 'calendar_reminder_recall',
    defaultMessage:
      "Learners who practice on multiple days recall up to 200% better than those who don't",
  },
  question: {
    id: 'calendar_reminder_question',
    defaultMessage: 'Would you like to set a reminder to strengthen your memories?',
  },
  yes: {
    id: 'calendar_reminder_yes',
    defaultMessage: 'Yes, set a reminder',
  },
  no: {
    id: 'calendar_reminder_no',
    defaultMessage: 'No, continue learning',
  },
  title: {
    id: 'calendar_reminder_schedule_title',
    defaultMessage: "I'll remind you tomorrow – what time works?",
  },
  mobileSubtitle: {
    id: 'calendar_reminder_schedule_subtitle_mobile',
    defaultMessage:
      "Attach it to something you already do, and we'll hold it in your calendar for a month.",
  },
  desktopSubtitle: {
    id: 'calendar_reminder_schedule_subtitle_desktop',
    defaultMessage:
      "A habit sticks when it's attached to something you already do every day. Pick the moment, and we'll hold it in your calendar for the next month.",
  },
  morningTitle: {
    id: 'calendar_reminder_morning_title',
    defaultMessage: 'With my morning coffee',
  },
  morningNote: {
    id: 'calendar_reminder_morning_note',
    defaultMessage: 'Before the day gets busy',
  },
  lunchTitle: {
    id: 'calendar_reminder_lunch_title',
    defaultMessage: 'On my lunch break',
  },
  lunchNote: {
    id: 'calendar_reminder_lunch_note',
    defaultMessage: 'A reset halfway through',
  },
  windDownTitle: {
    id: 'calendar_reminder_wind_down_title',
    defaultMessage: 'When I wind down',
  },
  windDownNote: {
    id: 'calendar_reminder_wind_down_note',
    defaultMessage: 'Around the time you finished today',
  },
  customTitle: {
    id: 'calendar_reminder_custom_title',
    defaultMessage: 'Another time',
  },
  customNote: {
    id: 'calendar_reminder_custom_note',
    defaultMessage: 'Pick an exact time',
  },
  morningHabit: {
    id: 'calendar_reminder_morning_habit',
    defaultMessage: "With my morning coffee, I'll do 15 minutes of {language}.",
  },
  lunchHabit: {
    id: 'calendar_reminder_lunch_habit',
    defaultMessage: "On my lunch break, I'll do 15 minutes of {language}.",
  },
  windDownHabit: {
    id: 'calendar_reminder_wind_down_habit',
    defaultMessage: "When I wind down for the evening, I'll do 15 minutes of {language}.",
  },
  customHabit: {
    id: 'calendar_reminder_custom_habit',
    defaultMessage: "At {time}, I'll do 15 minutes of {language}.",
  },
  planQuote: {
    id: 'calendar_reminder_plan_quote',
    defaultMessage: '“{habit}”',
  },
  yourPlan: {
    id: 'calendar_reminder_your_plan',
    defaultMessage: 'Your plan',
  },
  monthSummary: {
    id: 'calendar_reminder_month_summary',
    defaultMessage:
      "In a month: {hours} hours of {language} and about {words, number} new words at today's pace",
  },
  eventTitle: {
    id: 'calendar_reminder_event_title',
    defaultMessage: '{language} · Memrise',
  },
  eventSchedule: {
    id: 'calendar_reminder_event_schedule',
    defaultMessage: '{start} – {end} · repeats daily, {count} times',
  },
  google: {
    id: 'calendar_reminder_provider_google',
    defaultMessage: 'Google',
  },
  apple: {
    id: 'calendar_reminder_provider_apple',
    defaultMessage: 'Apple',
  },
  outlook: {
    id: 'calendar_reminder_provider_outlook',
    defaultMessage: 'Outlook',
  },
  addGoogle: {
    id: 'calendar_reminder_add_google',
    defaultMessage: 'Add to Google Calendar',
  },
  addApple: {
    id: 'calendar_reminder_add_apple',
    defaultMessage: 'Add to Apple Calendar',
  },
  addOutlook: {
    id: 'calendar_reminder_add_outlook',
    defaultMessage: 'Add to Outlook Calendar',
  },
  skip: {
    id: 'calendar_reminder_skip',
    defaultMessage: 'Skip for now',
  },
  continueLearning: {
    id: 'calendar_reminder_continue',
    defaultMessage: 'Continue learning',
  },
  back: {
    id: 'calendar_reminder_back',
    defaultMessage: 'Back',
  },
  streak: {
    id: 'calendar_reminder_streak',
    defaultMessage: '{count}-day streak',
  },
  pickTime: {
    id: 'calendar_reminder_pick_time',
    defaultMessage: 'Pick an exact time',
  },
});

const PROVIDERS: Array<{ id: CalendarProvider; label: keyof typeof msgs }> = [
  { id: 'google', label: 'google' },
  { id: 'apple', label: 'apple' },
  { id: 'outlook', label: 'outlook' },
];

const PROVIDER_CTAS = {
  google: msgs.addGoogle,
  apple: msgs.addApple,
  outlook: msgs.addOutlook,
} as const;

export interface CalendarReminderProps {
  languageName: string;
  wordsLearntToday: number;
  sessionsToday: number;
  streakCount: number;
  streakAccomplishedToday: boolean;
  finishedAt?: Date;
  /** Opens the schedule step. Used by Storybook. */
  initialStep?: 'prompt' | 'schedule';
  onContinue: () => void;
  onDecline: () => void;
}

export const CalendarReminder: FC<CalendarReminderProps> = ({
  languageName,
  wordsLearntToday,
  sessionsToday,
  streakCount,
  streakAccomplishedToday,
  finishedAt,
  initialStep = 'prompt',
  onContinue,
  onDecline,
}) => {
  const intl = useIntl();
  const { user } = useUserContext();
  const languagePairId = getLanguagePairId();
  const finishedAtRef = useRef(finishedAt ?? new Date());
  const [step, setStep] = useState<'prompt' | 'schedule'>(initialStep);
  const [slot, setSlot] = useState<SlotId>('windDown');
  const [provider, setProvider] = useState<CalendarProvider>('google');
  const [customTime, setCustomTime] = useState(toTimeInputValue(CUSTOM_DEFAULT_TIME));
  const [handedOff, setHandedOff] = useState(false);

  const windDownTime = useMemo(() => roundToNearestHalfHour(finishedAtRef.current), []);

  const track = useCallback(
    (action: CalendarReminderTrackingAction, trackingData?: Record<string, string>) => {
      void TrackingApi.trackUserAction(
        action,
        'calendar_reminder_screen',
        languagePairId,
        !!user?.isPro,
        trackingData,
      );
    },
    [languagePairId, user?.isPro],
  );

  const trackRef = useRef(track);
  trackRef.current = track;

  useEffect(() => {
    addToCalendarReminderStorageEntry(user);
    // Once per visit, so a refresh mid-flow does not show the prompt again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (step === 'prompt') {
      trackRef.current('prompt_viewed');
      return;
    }
    trackRef.current('screen_viewed', { step: 'schedule' });
  }, [step]);

  const formatClockTime = useCallback(
    (date: Date) => {
      const parts = new Intl.DateTimeFormat(intl.locale, {
        hour: 'numeric',
        minute: '2-digit',
      }).formatToParts(date);
      return parts
        .map(part => (part.type === 'dayPeriod' ? part.value.toLowerCase() : part.value))
        .join('');
    },
    [intl.locale],
  );

  const selectedTime: ClockTime =
    slot === 'morning'
      ? MORNING_TIME
      : slot === 'lunch'
        ? LUNCH_TIME
        : slot === 'windDown'
          ? windDownTime
          : parseTimeInputValue(customTime);

  const habit = intl.formatMessage(
    slot === 'morning'
      ? msgs.morningHabit
      : slot === 'lunch'
        ? msgs.lunchHabit
        : slot === 'windDown'
          ? msgs.windDownHabit
          : msgs.customHabit,
    {
      language: languageName,
      time: formatClockTime(new Date(2026, 0, 1, selectedTime.hours, selectedTime.minutes, 0)),
    },
  );

  const event = useMemo(
    () =>
      buildCalendarEvent({
        title: intl.formatMessage(msgs.eventTitle, { language: languageName }),
        description: habit,
        time: selectedTime,
        now: finishedAtRef.current,
        timeZone: browserTimeZone(),
      }),
    // habit and selectedTime are derived each render; the memo keeps the event stable
    // for the same slot, custom time, and language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [habit, languageName, slot, customTime, intl],
  );

  const slotTimeLabel = (time: ClockTime) =>
    formatClockTime(new Date(2026, 0, 1, time.hours, time.minutes, 0));

  const decline = () => {
    track('clicked_no');
    onDecline();
  };

  const skip = () => {
    track('clicked_skip');
    onDecline();
  };

  const handleClose = () => {
    if (step === 'schedule' && !handedOff) {
      setStep('prompt');
      return;
    }
    if (handedOff) {
      onContinue();
      return;
    }
    decline();
  };

  const handleAdd = () => {
    track('reminder_set', { provider });
    openProviderCalendar(provider, event);
    setHandedOff(true);
  };

  const slots: Array<{ id: SlotId; title: string; note: string; time: ClockTime }> = [
    {
      id: 'morning',
      title: intl.formatMessage(msgs.morningTitle),
      note: intl.formatMessage(msgs.morningNote),
      time: MORNING_TIME,
    },
    {
      id: 'lunch',
      title: intl.formatMessage(msgs.lunchTitle),
      note: intl.formatMessage(msgs.lunchNote),
      time: LUNCH_TIME,
    },
    {
      id: 'windDown',
      title: intl.formatMessage(msgs.windDownTitle),
      note: intl.formatMessage(msgs.windDownNote),
      time: windDownTime,
    },
    {
      id: 'custom',
      title: intl.formatMessage(msgs.customTitle),
      note: intl.formatMessage(msgs.customNote),
      time: parseTimeInputValue(customTime),
    },
  ];

  return (
    <Modal isOpen fullSize hideCloseButton handleClose={handleClose}>
      <S.Page data-testid="calendar-reminder-screen">
        <S.Header $dark={step === 'schedule'}>
          {step === 'schedule' && (
            <S.BackButton
              type="button"
              onClick={() => setStep('prompt')}
              aria-label={intl.formatMessage(msgs.back)}
              data-testid="calendar-reminder-back"
            >
              <Icon name="chevronLeft" />
            </S.BackButton>
          )}
          <S.Logo name="memrise" $hideOnMobile={step === 'schedule'} />
          <S.Streak>
            <Icon name={streakAccomplishedToday ? 'fire' : 'fireDisabled'} />
            <span>{intl.formatMessage(msgs.streak, { count: streakCount })}</span>
          </S.Streak>
        </S.Header>

        <S.Body>
          {step === 'prompt' ? (
            <S.Prompt data-testid="calendar-reminder-prompt">
              <S.MascotWrap>
                <Mascot />
              </S.MascotWrap>
              <S.SuccessPill dir="auto">
                <Icon name="check" />
                {intl.formatMessage(msgs.wellDone, {
                  count: wordsLearntToday,
                  language: languageName,
                })}
              </S.SuccessPill>
              <S.Remember>{intl.formatMessage(msgs.remember)}</S.Remember>
              <S.Percent>{intl.formatMessage(msgs.percent)}</S.Percent>
              <S.Better>{intl.formatMessage(msgs.better)}</S.Better>
              <S.Recall>{intl.formatMessage(msgs.recall)}</S.Recall>
              <S.Question>{intl.formatMessage(msgs.question)}</S.Question>
              <S.PromptActions>
                <Button type="button" buttonType="secondary" buttonSize="large" onClick={decline}>
                  {intl.formatMessage(msgs.no)}
                </Button>
                <Button
                  type="button"
                  buttonType="primary"
                  buttonSize="large"
                  onClick={() => {
                    track('clicked_yes');
                    setStep('schedule');
                  }}
                >
                  {intl.formatMessage(msgs.yes)}
                </Button>
              </S.PromptActions>
            </S.Prompt>
          ) : (
            <S.ScheduleLayout data-testid="calendar-reminder-schedule">
              <div>
                <S.Title>{intl.formatMessage(msgs.title)}</S.Title>
                <S.Subtitle>{intl.formatMessage(msgs.mobileSubtitle)}</S.Subtitle>
                <S.Subtitle $desktopOnly>{intl.formatMessage(msgs.desktopSubtitle)}</S.Subtitle>
                <S.Options role="radiogroup" aria-label={intl.formatMessage(msgs.title)}>
                  {slots.map(option => {
                    const selected = slot === option.id;
                    return (
                      <S.Option key={option.id} $selected={selected}>
                        <S.Radio
                          type="radio"
                          name="calendar-reminder-slot"
                          value={option.id}
                          checked={selected}
                          onChange={() => setSlot(option.id)}
                        />
                        <S.RadioMark $selected={selected}>
                          {selected && <Icon name="check" />}
                        </S.RadioMark>
                        <S.OptionText>
                          <S.OptionTitle>{option.title}</S.OptionTitle>
                          <S.OptionNote>{option.note}</S.OptionNote>
                        </S.OptionText>
                        {option.id === 'custom' && selected ? (
                          <S.TimeInput
                            type="time"
                            value={customTime}
                            aria-label={intl.formatMessage(msgs.pickTime)}
                            onChange={change => setCustomTime(change.target.value)}
                          />
                        ) : (
                          <S.OptionTime>{slotTimeLabel(option.time)}</S.OptionTime>
                        )}
                      </S.Option>
                    );
                  })}
                </S.Options>
              </div>

              <div>
                <S.PlanCard>
                  <S.Eyebrow>{intl.formatMessage(msgs.yourPlan)}</S.Eyebrow>
                  <S.Quote dir="auto">{intl.formatMessage(msgs.planQuote, { habit })}</S.Quote>
                  <S.MonthLine>
                    <S.MonthIcon name="idea" />
                    <span>
                      {intl.formatMessage(msgs.monthSummary, {
                        hours: intl.formatNumber(REMINDER_HOURS_IN_A_MONTH, {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        }),
                        language: languageName,
                        words: monthlyNewWords(wordsLearntToday, sessionsToday),
                      })}
                    </span>
                  </S.MonthLine>
                  <S.EventPreview>
                    <S.EventTitle>
                      {intl.formatMessage(msgs.eventTitle, { language: languageName })}
                    </S.EventTitle>
                    <S.EventSchedule>
                      {intl.formatMessage(msgs.eventSchedule, {
                        start: formatClockTime(event.start),
                        end: formatClockTime(event.end),
                        count: REMINDER_REPEAT_COUNT,
                      })}
                    </S.EventSchedule>
                  </S.EventPreview>
                  <S.Providers role="radiogroup" aria-label={intl.formatMessage(msgs.yourPlan)}>
                    {PROVIDERS.map(item => {
                      const selected = provider === item.id;
                      return (
                        <S.Provider key={item.id} $selected={selected}>
                          <S.Radio
                            type="radio"
                            name="calendar-reminder-provider"
                            value={item.id}
                            checked={selected}
                            onChange={() => setProvider(item.id)}
                          />
                          {intl.formatMessage(msgs[item.label])}
                        </S.Provider>
                      );
                    })}
                  </S.Providers>
                </S.PlanCard>
                <S.Actions>
                  <Button
                    type="button"
                    buttonType="primary"
                    buttonSize="large"
                    onClick={handedOff ? onContinue : handleAdd}
                    data-testid="calendar-reminder-add"
                  >
                    {handedOff
                      ? intl.formatMessage(msgs.continueLearning)
                      : intl.formatMessage(PROVIDER_CTAS[provider])}
                  </Button>
                  {!handedOff && (
                    <S.SkipButton type="button" onClick={skip}>
                      {intl.formatMessage(msgs.skip)}
                    </S.SkipButton>
                  )}
                </S.Actions>
              </div>
            </S.ScheduleLayout>
          )}
        </S.Body>
      </S.Page>
    </Modal>
  );
};
